import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  decodeTrack,
  buildGpx,
  formatDuration,
  formatNumber,
  formatDistanceUnit,
  speedValue,
  speedUnitLabel,
  type TrackPoint,
} from '@boatnav/core';
import { MapShell, useMapController } from '../map/MapShell.js';
import { getTrackMeta, getTrackPoints, renameTrack, deleteTrack, type TrackMeta } from '../../lib/db.js';
import { formatDateTime } from '../../lib/datetime.js';
import { useUnitsStore } from '../../app/unitsStore.js';
import { exportGpx } from './gpxExport.js';
import { msg } from '../../lib/messages.js';
import './track-detail.css';

function TrackOverlay({ points }: { points: TrackPoint[] }): null {
  const controller = useMapController();
  useEffect(() => {
    if (!controller) return;
    controller.drawTrack(points.map((p) => [p.lat, p.lon] as [number, number]));
    return () => controller.clearTrack();
  }, [controller, points]);
  return null;
}

export function TrackDetailScreen(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const units = useUnitsStore();
  const navigate = useNavigate();
  const [meta, setMeta] = useState<TrackMeta | null>(null);
  const [points, setPoints] = useState<TrackPoint[]>([]);
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    void (async () => {
      const [m, buf] = await Promise.all([getTrackMeta(id), getTrackPoints(id)]);
      if (!alive) return;
      if (m) {
        setMeta(m);
        setNameDraft(m.name);
      }
      if (buf) setPoints(decodeTrack(new Uint8Array(buf)));
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  if (!meta) return <div className="track-detail track-detail--loading" />;

  const onRename = async (): Promise<void> => {
    const name = nameDraft.trim();
    if (name && id) {
      await renameTrack(id, name);
      setMeta({ ...meta, name });
    }
    setRenaming(false);
  };

  const onDelete = async (): Promise<void> => {
    if (id) await deleteTrack(id);
    navigate('/turer');
  };

  const onExport = (): void => {
    const result = exportGpx(meta.name, buildGpx({ name: meta.name, points }));
    if (result === 'opened') setNotice(msg.tracks.gpxTabHint);
  };

  return (
    <div className="track-detail">
      <div className="track-detail__map">
        <MapShell>
          <TrackOverlay points={points} />
        </MapShell>
      </div>

      <div className="track-detail__panel">
        <button type="button" className="track-detail__back" onClick={() => navigate('/turer')}>
          ‹ {msg.nav.tabTracks}
        </button>
        <h1 className="track-detail__name numeral">{meta.name}</h1>
        <div className="track-detail__date">{formatDateTime(meta.startedAt)}</div>

        <div className="track-detail__stats">
          <Stat
            label={msg.tracks.stats.distance}
            value={formatDistanceUnit(meta.distanceNm, units.distance)}
          />
          <Stat label={msg.tracks.stats.duration} value={formatDuration(meta.durationS / 60)} />
          <Stat
            label={msg.tracks.stats.avg}
            value={`${formatNumber(speedValue(meta.avgKn, units.speed), 1)} ${speedUnitLabel[units.speed]}`}
          />
          <Stat
            label={msg.tracks.stats.max}
            value={`${formatNumber(speedValue(meta.maxKn, units.speed), 1)} ${speedUnitLabel[units.speed]}`}
          />
        </div>

        {notice && <p className="track-detail__notice">{notice}</p>}

        <div className="track-detail__actions">
          <button type="button" className="btn btn--ghost" onClick={() => setRenaming(true)}>
            {msg.tracks.rename}
          </button>
          <button type="button" className="btn btn--ghost" onClick={onExport}>
            {msg.tracks.exportGpx}
          </button>
          <button type="button" className="btn btn--ghost track-detail__delete" onClick={() => setConfirmDelete(true)}>
            {msg.tracks.delete}
          </button>
        </div>
      </div>

      {renaming && (
        <div className="sheet-backdrop" onClick={() => setRenaming(false)}>
          <div className="sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="sheet__handle" />
            <h2 className="sheet__title">{msg.tracks.rename}</h2>
            <input
              className="track-detail__input"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              maxLength={120}
              autoFocus
            />
            <div className="sheet__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setRenaming(false)}>
                {msg.tracks.cancel}
              </button>
              <button type="button" className="btn btn--accent" onClick={onRename}>
                {msg.tracks.rename}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="sheet-backdrop" onClick={() => setConfirmDelete(false)}>
          <div className="sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="sheet__handle" />
            <h2 className="sheet__title">{msg.tracks.deleteConfirm}</h2>
            <div className="sheet__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setConfirmDelete(false)}>
                {msg.tracks.cancel}
              </button>
              <button type="button" className="btn btn--accent" onClick={onDelete}>
                {msg.tracks.delete}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="track-detail__stat">
      <div className="track-detail__stat-label">{label}</div>
      <div className="track-detail__stat-value numeral">{value}</div>
    </div>
  );
}
